import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Plus, Minus, MapPin, Calendar, Award } from 'lucide-react';
import SEO from '../components/SEO';
import { FAQ_QUESTIONS, FAQ_PAGE_SCHEMA } from '../data/faq';
import { ORGANIZATION_SCHEMA } from '../constants/seo';
import { STRIPE_CHECKOUT_URL, DISCOVERY_CALL_URL } from '../constants/site';

const FAQ_JSONLD = {
  '@context': 'https://schema.org',
  '@graph': [ORGANIZATION_SCHEMA, FAQ_PAGE_SCHEMA],
};

const FAQItem = ({ item, index, isOpen, onToggle }) => (
  <div
    data-testid={`faq-item-${index}`}
    className="border-b border-[#E5E0D8]"
  >
    <button
      onClick={onToggle}
      data-testid={`faq-question-${index}`}
      className="w-full flex items-start justify-between gap-6 py-7 text-left group"
      aria-expanded={isOpen}
    >
      <h3 className="font-heading text-lg sm:text-xl text-[#0F0F0F] leading-snug group-hover:text-[#C5A880] transition-colors">
        {item.q}
      </h3>
      <span className="flex-shrink-0 mt-1 text-[#C5A880]">
        {isOpen ? <Minus size={22} strokeWidth={1.5} /> : <Plus size={22} strokeWidth={1.5} />}
      </span>
    </button>
    {isOpen && (
      <div
        data-testid={`faq-answer-${index}`}
        className="pb-8 pr-10 text-base text-[#1A1A1A] leading-relaxed"
      >
        {item.a}
      </div>
    )}
  </div>
);

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div data-testid="faq-page" className="min-h-screen bg-[#FDFBF7]">
      <SEO
        title="VIP Flight Attendant Training FAQ — France, Europe & Online"
        description="Where to train as a VIP flight attendant in France, cost, salary, EU Cabin Crew Attestation, best operators, online options. 14 answers from Gate 33 — private aviation training provider based in Nice, France."
        path="/faq"
        keywords="VIP Flight Attendant Training France FAQ, Private Jet Cabin Crew Training FAQ, VIP flight attendant salary Europe, EU Cabin Crew Attestation private aviation, best VIP flight attendant training Europe, how to become private jet flight attendant"
        jsonLd={FAQ_JSONLD}
      />

      {/* ===== HERO ===== */}
      <section data-testid="faq-hero" className="border-b border-[#E5E0D8] pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <span className="overline inline-block mb-6">Frequently Asked Questions</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight text-[#0F0F0F] mb-8">
            Where to train as a VIP flight attendant in France — and everything else candidates ask.
          </h1>
          <p className="text-lg text-[#1A1A1A] leading-relaxed max-w-3xl mb-6">
            Real answers to the fourteen questions candidates most often type into Google and ChatGPT when researching a career in private aviation — salary, cost, licensing, best operators, and where to train.
          </p>
          <div className="flex flex-wrap items-center gap-6 text-sm text-[#5A5A5A] mt-8">
            <span className="inline-flex items-center gap-2">
              <MapPin size={16} className="text-[#C5A880]" />
              Based in Nice, France
            </span>
            <span className="inline-flex items-center gap-2">
              <Calendar size={16} className="text-[#C5A880]" />
              Live online across Europe
            </span>
            <span className="inline-flex items-center gap-2">
              <Award size={16} className="text-[#C5A880]" />
              Founded by a working corporate flight attendant
            </span>
          </div>
        </div>
      </section>

      {/* ===== FAQ LIST ===== */}
      <section data-testid="faq-list" className="py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="border-t border-[#E5E0D8]">
            {FAQ_QUESTIONS.map((item, i) => (
              <FAQItem
                key={i}
                item={item}
                index={i}
                isOpen={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ===== CROSS-LINKS ===== */}
      <section data-testid="faq-cross-links" className="bg-white border-y border-[#E5E0D8] py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <span className="overline inline-block mb-4">Read further</span>
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight leading-tight text-[#0F0F0F] mb-8">
            Editorial pieces behind the answers.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to="/insights/field-notes/eu-vip-flight-attendant-24000-euros-a-year"
              data-testid="faq-link-24k"
              className="block border border-[#E5E0D8] p-6 bg-[#FDFBF7] hover:border-[#C5A880] transition-colors group"
            >
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#C5A880] font-semibold">Field Notes</span>
              <h3 className="font-heading text-lg text-[#0F0F0F] mt-2 mb-3 group-hover:text-[#C5A880] transition-colors">
                EU VIP Flight Attendant at €24,000 a Year? Read This Before You Accept It.
              </h3>
              <p className="text-sm text-[#5A5A5A] leading-relaxed">Salary reality, the three operator tiers, contract details ads rarely mention.</p>
            </Link>
            <Link
              to="/insights/field-notes/private-aviation-is-about-operations"
              data-testid="faq-link-operations"
              className="block border border-[#E5E0D8] p-6 bg-[#FDFBF7] hover:border-[#C5A880] transition-colors group"
            >
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#C5A880] font-semibold">Field Notes</span>
              <h3 className="font-heading text-lg text-[#0F0F0F] mt-2 mb-3 group-hover:text-[#C5A880] transition-colors">
                OK. Stop. Private aviation isn&apos;t about service. It&apos;s about operations.
              </h3>
              <p className="text-sm text-[#5A5A5A] leading-relaxed">The 80% of the job candidates don&apos;t see — and why operators hire for it.</p>
            </Link>
          </div>
          <div className="mt-8">
            <Link
              to="/insights"
              data-testid="faq-link-insights"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] font-semibold text-[#0F0F0F] hover:text-[#C5A880] transition-colors"
            >
              All Field Notes <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section data-testid="faq-cta" className="section-dark py-16 md:py-20">
        <div className="container-custom text-center max-w-3xl">
          <span className="text-xs uppercase tracking-[0.2em] font-medium text-[#C5A880] inline-block mb-4">
            Still have questions?
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-6 text-[#FDFBF7]">
            Book a free 45-minute call.
          </h2>
          <p className="text-[#9A9A9A] leading-relaxed mb-10 max-w-xl mx-auto">
            We&apos;ll assess your profile, walk through the programme, and confirm alignment before you enroll. No obligation.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href={DISCOVERY_CALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="faq-cta-book-call"
              className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-10 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300"
            >
              Book a Discovery Call <ArrowRight size={16} />
            </a>
            <Link
              to="/academy"
              data-testid="faq-cta-see-training"
              className="inline-flex items-center gap-2 border border-[#C5A880] text-[#C5A880] px-10 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#C5A880] hover:text-[#0F0F0F] transition-all duration-300"
            >
              See the 3-Day Online Training <ArrowRight size={16} />
            </Link>
          </div>
          <p className="mt-8 text-xs text-[#9A9A9A]">
            €600 or up to 24× payments with PayPal · Live online, based in Nice, France.
          </p>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
